/**
 * 대시보드 월 달력 WebP: 최초 fetch 후 IndexedDB + 메모리 Blob URL로 재사용.
 * 버셀 public/calendar → Storage 폴백. IndexedDB·메모리 키는 파일 인덱스라 출처가 바뀌어도 재사용한다.
 */

import {
  dashboardMonthCalendarArtFallbackUrl,
  dashboardMonthCalendarArtUrl,
} from './dashboardCalendarArt';

const DB_NAME = 'ClassPlannerDashboardCalendarArtCache';
const DB_VERSION = 1;
const STORE = 'images';

/** 에셋/변환 스펙이 바뀌면 올려서 재다운로드 */
export const DASHBOARD_CALENDAR_ART_CACHE_VERSION = 'webp-640-v1';

type ArtCacheRecord = {
  buffer: ArrayBuffer;
  version: string;
  mime: string;
};

type MemoryEntry = {
  blobUrl: string;
  version: string;
};

const memoryByKey = new Map<string, MemoryEntry>();
const inflightByKey = new Map<string, Promise<string | null>>();

function cacheKey(index: number): string {
  return `calendar${index}`;
}

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<ArtCacheRecord | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<ArtCacheRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as ArtCacheRecord | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, record: ArtCacheRecord): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(record, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

async function fetchImageArrayBuffer(
  sourceUrl: string
): Promise<{ buffer: ArrayBuffer; mime: string } | null> {
  try {
    const res = await fetch(sourceUrl, {
      method: 'GET',
      cache: 'force-cache',
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    if (buffer.byteLength === 0) return null;
    const mime =
      res.headers.get('content-type')?.split(';')[0]?.trim() || 'image/webp';
    return { buffer, mime };
  } catch {
    return null;
  }
}

function remember(key: string, version: string, buffer: ArrayBuffer, mime: string): string {
  const prev = memoryByKey.get(key);
  if (prev && prev.version !== version) {
    try {
      URL.revokeObjectURL(prev.blobUrl);
    } catch {
      /* ignore */
    }
  }
  const blobUrl = URL.createObjectURL(new Blob([buffer], { type: mime || 'image/webp' }));
  memoryByKey.set(key, { blobUrl, version });
  return blobUrl;
}

/** 동기 peek — 메모리에 같은 버전이 있으면 즉시 반환 */
export function peekCachedDashboardCalendarArtUrl(index: number): string | null {
  if (!Number.isFinite(index)) return null;
  const key = cacheKey(index);
  const mem = memoryByKey.get(key);
  if (mem && mem.version === DASHBOARD_CALENDAR_ART_CACHE_VERSION) return mem.blobUrl;
  return null;
}

/**
 * @returns Blob URL — 호출 측에서 revoke 하지 말 것(세션 동안 재사용).
 */
export async function getCachedDashboardCalendarArtUrl(index: number): Promise<string | null> {
  if (!Number.isFinite(index)) return null;
  const key = cacheKey(index);
  const version = DASHBOARD_CALENDAR_ART_CACHE_VERSION;

  const mem = memoryByKey.get(key);
  if (mem && mem.version === version) return mem.blobUrl;

  let inflight = inflightByKey.get(key);
  if (!inflight) {
    inflight = (async (): Promise<string | null> => {
      try {
        const cached = await idbGet(key);
        if (
          cached &&
          cached.buffer?.byteLength > 0 &&
          cached.version === version
        ) {
          return remember(key, version, cached.buffer, cached.mime || 'image/webp');
        }
      } catch {
        /* IDB 미지원·거부 */
      }

      const primary = dashboardMonthCalendarArtUrl(index);
      const fallback = dashboardMonthCalendarArtFallbackUrl(index);
      let got = await fetchImageArrayBuffer(primary);
      if (!got && fallback !== primary) {
        got = await fetchImageArrayBuffer(fallback);
      }
      if (!got) {
        try {
          const stale = await idbGet(key);
          if (stale?.buffer?.byteLength) {
            return remember(key, stale.version, stale.buffer, stale.mime || 'image/webp');
          }
        } catch {
          /* ignore */
        }
        return null;
      }

      try {
        await idbPut(key, {
          buffer: got.buffer,
          version,
          mime: got.mime,
        });
      } catch {
        /* 저장 실패해도 표시는 가능 */
      }
      return remember(key, version, got.buffer, got.mime);
    })().finally(() => {
      inflightByKey.delete(key);
    });
    inflightByKey.set(key, inflight);
  }
  return inflight;
}

/** 백그라운드 프리로드 */
export function preloadDashboardCalendarArt(indices: number[]): void {
  void Promise.all(indices.map((i) => getCachedDashboardCalendarArtUrl(i)));
}
